// The cyber backdrop v6: an ORIGINAL composition at Master Duel craft — not
// MD's red/blue diagonal (v5 copied it; rejected). This is LINK VRAINS' own
// iconography: a dark cyberspace sky over a perspective data-sea. Grid lines
// converge to a vanishing point right-of-centre and scroll toward a glowing
// horizon; data packets race down the lanes; auroral updrafts rise off the
// sea into the sky; a colossal ghost gate-ring turns slowly behind the hero.
// The sky (where all UI text lives) stays near-black — the drama is banked
// in the bottom third and the horizon band, so contrast comes free.
// Colours anchor to the app tokens (void/cyan/magenta).
//
// WebGPU-only (no WebGL fallback by design). The shader is authored in TSL —
// a 1:1 port of the original GLSL — and compiled to WGSL by three.
//
// Respects prefers-reduced-motion: renders one static frame, no RAF loop.

import * as THREE from "three/webgpu";
import {
  Fn, uniform, float, vec2, vec3, vec4, positionGeometry, screenCoordinate, screenSize,
  fract, floor, sin, cos, atan, dot, exp, pow, length, sign, abs, max, min, clamp, mix, step, smoothstep,
} from "three/tsl";
import { settings, effectiveTier } from "../stores/settings.svelte.ts";

// TSL nodes are deep generic chains (Node<"float">, VarNode<…>, …); this shader is structural math
// ported 1:1 from GLSL, so a loose node alias keeps it readable — the runtime validates types anyway.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

// ── shader helpers (each call inlines its expression tree, like the GLSL compiler did) ──
const hash21 = (p: N): N => {
  const a: N = fract(p.mul(vec2(234.34, 435.345)));
  const b: N = a.add(dot(a, a.add(34.23)));
  return fract(b.x.mul(b.y));
};
const vnoise = (p: N): N => {
  const i: N = floor(p);
  const f0: N = fract(p);
  const f: N = f0.mul(f0).mul(f0.mul(-2.0).add(3.0));
  return mix(
    mix(hash21(i), hash21(i.add(vec2(1, 0))), f.x),
    mix(hash21(i.add(vec2(0, 1))), hash21(i.add(vec2(1, 1))), f.x),
    f.y,
  );
};
// fractal value noise — soft cloud / nebula field (rotated octaves kill the grid look).
// The 0.6-rad octave rotation is constant, so it folds to plain JS numbers (no in-shader mat2).
const fbm = (p0: N): N => {
  const c = Math.cos(0.6), s = Math.sin(0.6);
  let p = p0;
  let v: N | null = null;
  let a = 0.55;
  for (let o = 0; o < 4; o++) {
    const term = vnoise(p).mul(a);
    v = v ? v.add(term) : term;
    p = vec2(p.x.mul(c).add(p.y.mul(s)), p.x.mul(-s).add(p.y.mul(c))).mul(2.03);
    a *= 0.5;
  }
  return v!;
};
// signed distance to a triangle (branchless: the GLSL `if` becomes a mix on its condition)
const sdTri = (p: N, r: N): N => {
  const k = 1.7320508;
  const px0 = abs(p.x).sub(r);
  const py0 = p.y.add(r.div(k));
  const cond = step(0.0, px0.add(py0.mul(k)));
  const px1 = mix(px0, px0.sub(py0.mul(k)).div(2.0), cond);
  const py1 = mix(py0, px0.mul(-k).sub(py0).div(2.0), cond);
  const px2 = px1.sub(clamp(px1, r.mul(-2.0), 0.0));
  return length(vec2(px2, py1)).negate().mul(sign(py1));
};
// 1px-crisp band: d = distance, w = half-width, px = pixel size in d's units.
const crisp = (d: N, w: N, px: N): N => smoothstep(w.sub(px), w.add(px), d).oneMinus();

export class CyberWorld {
  private renderer: THREE.WebGPURenderer;
  private scene = new THREE.Scene();
  // any camera with updateProjectionMatrix() satisfies WebGPURenderer — the raw clip-space
  // vertexNode ignores its matrices entirely (a bare THREE.Camera throws in the WebGPU path)
  private camera = new THREE.OrthographicCamera();
  private material: THREE.MeshBasicNodeMaterial;
  private geometry: THREE.BufferGeometry;
  private uTime = uniform(12.0); // a lively moment for the reduced-motion still
  private t0 = performance.now();
  private raf = 0;
  private osReduced: boolean;
  // A static, no-RAF backdrop when: OS prefers-reduced-motion, the in-app "reduce effects" toggle, OR
  // the Low quality tier — the backdrop is a fullscreen procedural shader, so on a weak machine its
  // ambient loop is pure heat "while doing nothing" on a menu (and doubles up behind a 3D duel).
  private get reduced(): boolean { return this.osReduced || settings.reducedEffects || effectiveTier() === "low"; }
  private disposed = false;
  private ready = false;

  constructor(private canvas: HTMLCanvasElement) {
    this.osReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this.renderer = new THREE.WebGPURenderer({
      canvas,
      antialias: false, // fullscreen shader, nothing to multisample
      alpha: false,
      powerPreference: "high-performance",
    });
    // Ambience, not content: cap at 1.25× device px — the soft grid/ring backdrop reads identically
    // at that density, and DPR 2 → 1.25 cuts the fullscreen hash-math fill by ~60%. (This scene runs
    // BEHIND the duel board / world at all times, so its budget must stay small.)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.25));
    // The GLSL original (RawShaderMaterial) wrote its colour bytes RAW; the node pipeline would add a
    // linear→sRGB encode that lifts the whole composition. Linear output = byte-for-byte parity.
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace;

    // DoubleSide: the raw clip-space vertexNode bypasses three's winding compensation, and WebGPU's
    // flipped framebuffer convention would otherwise back-face-cull the fullscreen triangle.
    this.material = new THREE.MeshBasicNodeMaterial({ depthTest: false, depthWrite: false, side: THREE.DoubleSide });
    this.material.vertexNode = vec4(positionGeometry, 1.0); // clip-space passthrough (fullscreen triangle)
    this.material.fragmentNode = this.buildFragment();
    // One triangle covering the screen (no quad diagonal seam, no camera math).
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3),
    );
    const mesh = new THREE.Mesh(this.geometry, this.material);
    mesh.frustumCulled = false;
    this.scene.add(mesh);
    this.renderer.init().then(
      () => { if (!this.disposed) { this.ready = true; this.renderOnce(); } },
      (err: unknown) => console.error("[cyberworld] WebGPU init failed — backdrop disabled (no WebGL fallback by design):", err),
    );
    this.resize();
  }

  /** The whole backdrop as one TSL fragment program — a 1:1 port of the original GLSL. */
  private buildFragment(): N {
    const uTime = this.uTime;
    return Fn(() => {
      // screenCoordinate is top-left origin (WebGPU convention); this composition was authored
      // against GL's bottom-left gl_FragCoord, so flip Y once and everything below ports verbatim.
      const frag: N = vec2(screenCoordinate.x, screenSize.y.sub(screenCoordinate.y));
      const uv: N = frag.div(screenSize);
      const sv: N = frag.sub(screenSize.mul(0.5)).div(screenSize.y);
      const px: N = float(1.0).div(screenSize.y); // one pixel, in sv units — everything crisp keys off this

      const HY = -0.14; // horizon height
      const VX = 0.30;  // vanishing point (right-of-centre, under the hero)
      // NAVY-blue palette. Highlights are luminous BLUE — never white.
      const navyC = vec3(0.02, 0.06, 0.20);  // deep navy — the sky body
      const cyanC = vec3(0.16, 0.52, 1.00);  // electric blue — structure / lines
      const glowC = vec3(0.26, 0.58, 1.00);  // luminous royal blue — cores / glints
      const deepC = vec3(0.05, 0.14, 0.50);  // saturated mid-navy for washes

      // deep navy sky, richer toward the top, settling near-black at the horizon
      const col = mix(vec3(0.008, 0.016, 0.055), vec3(0.020, 0.055, 0.150),
        smoothstep(-0.5, 0.62, sv.y)).toVar();

      // ── nebula: slow volumetric cloudbanks give the navy sky real depth ──
      const nebFall = smoothstep(HY - 0.04, 0.6, sv.y);
      const neb = smoothstep(0.38, 0.95, fbm(vec2(sv.x.mul(1.25).add(uTime.mul(0.012)), sv.y.mul(1.55).sub(uTime.mul(0.006)))));
      col.addAssign(mix(deepC, cyanC, 0.30).mul(neb.mul(nebFall).mul(0.40)));
      const neb2 = smoothstep(0.50, 1.0, fbm(vec2(sv.x.mul(2.7).sub(4.0), sv.y.mul(2.9).add(uTime.mul(0.009)))));
      col.addAssign(deepC.mul(neb2.mul(nebFall).mul(0.22)));

      // ── volumetric god-rays fanning up out of the vanishing point ──
      const toVp = sv.sub(vec2(VX, HY));
      const ang2 = atan(toVp.x, max(toVp.y, 1e-4));
      const rayN = fbm(vec2(ang2.mul(3.2), uTime.mul(0.03)));
      const rays = pow(sin(ang2.mul(24.0).add(rayN.mul(6.0))).mul(0.5).add(0.5), 4.0);
      col.addAssign(glowC.mul(rays.mul(exp(length(toVp).mul(-1.5))).mul(smoothstep(HY, 0.6, sv.y)).mul(0.16)));

      const below = step(sv.y, HY);
      const depth = max(float(HY).sub(sv.y), 1e-4);

      // ── the data-sea: perspective grid scrolling toward the horizon ──────────
      // (the GLSL `if (sv.y < HY)` block, made branchless: every term is gated by `below`)
      {
        const wu = sv.x.sub(VX).div(depth);        // across the sea
        const wz = float(0.22).div(depth);         // into the screen
        const pxu = px.div(depth);                 // analytic pixel sizes (no derivatives)
        const pxz = px.mul(0.22).div(depth.mul(depth));

        const su = 1.6, sz = 1.0;                  // lane spacings
        const fu0 = fract(wu.div(su));
        const fu = min(fu0, fu0.oneMinus()).mul(su);
        const fz0 = fract(wz.div(sz).add(uTime.mul(0.10)));
        const fz = min(fz0, fz0.oneMinus()).mul(sz);
        const lu = crisp(fu, pxu.mul(0.8), pxu.mul(1.2));
        const lz = crisp(fz, pxz.mul(0.8), pxz.mul(1.2));

        const fogN = exp(depth.mul(-5.5));         // energy concentrates at the horizon
        const baseA = fogN.mul(0.62).add(0.14);
        const core = exp(pow(sv.x.sub(VX).mul(1.4), 2.0).negate()); // brightens toward the VP
        const gridTint = mix(cyanC, glowC, core.mul(0.5));
        col.addAssign(gridTint.mul(max(lu, lz).mul(baseA).mul(below)));

        // data packets racing down the converging lanes toward the horizon
        const lane = floor(wu.div(su).add(0.5));
        const lh = hash21(vec2(lane, 17.0));
        const pulse = exp(pow(fract(wz.mul(0.25).sub(lh.mul(3.0)).sub(uTime.mul(0.15))).sub(0.5).mul(9.0), 2.0).negate());
        col.addAssign(mix(cyanC, glowC, 0.6).mul(lu.mul(pulse).mul(fogN.mul(0.90).add(0.35)).mul(step(0.35, lh)).mul(below)));

        // sea-glow so the floor never reads as flat black between lines
        col.addAssign(gridTint.mul(fogN.mul(0.18).mul(below)));
      }

      // ── horizon: a luminous BLUE band, brightening at the vanishing point ──
      const hb = exp(abs(sv.y.sub(HY)).mul(-26.0));
      const hbw = exp(abs(sv.y.sub(HY)).mul(-7.0));
      const horTint = mix(cyanC, glowC, exp(pow(sv.x.sub(VX).mul(1.1), 2.0).negate()).mul(0.6));
      col.addAssign(horTint.mul(hb.mul(0.55).add(hbw.mul(0.13)).mul(sin(uTime.mul(0.5)).mul(0.08).add(0.92))));
      col.addAssign(glowC.mul(exp(pow(length(sv.sub(vec2(VX, HY))).mul(3.0), 2.0).negate()).mul(0.30)));
      // a thin bright core line — luminous blue, not white
      col.addAssign(mix(cyanC, glowC, 0.7).mul(crisp(abs(sv.y.sub(HY)), float(0.0014), px).mul(0.34)));
      // a soft stage-light rising off the vanishing point behind the hero
      col.addAssign(deepC.mul(exp(pow(length(sv.sub(vec2(VX, 0.05))).mul(1.3), 2.0).negate()).mul(0.10)));

      // ── auroral updrafts: data rising off the sea, dissolving upward ─────────
      {
        // one layer of auroral updraft columns rising off the sea into the sky
        const updraft = (lanes: number, seed: number): { u: N; laneH: N } => {
          const lane = floor(sv.x.mul(lanes).add(seed));
          const laneH = hash21(vec2(lane, seed + 4.2));
          const lx = fract(sv.x.mul(lanes).add(seed)).sub(0.5);
          const body = exp(lx.mul(lx).mul(-26.0));
          const riseArg: N = max(sv.y.sub(HY), 0.0).mul(laneH.mul(5.0).add(1.6)).negate();
          const rise = exp(riseArg);
          const shimmer = vnoise(vec2(lane.mul(7.3), sv.y.mul(2.5).sub(uTime.mul(laneH.mul(0.12).add(0.10))))).mul(0.45).add(0.55);
          return { u: body.mul(rise).mul(shimmer).mul(step(0.55, laneH)), laneH };
        };
        const u1 = updraft(9.0, 0.0);
        const u2 = updraft(17.0, 5.7);
        const above = below.oneMinus();
        col.addAssign(mix(cyanC, deepC, u1.laneH).mul(u1.u.mul(0.20).mul(above)));
        col.addAssign(mix(cyanC, deepC, u2.laneH).mul(u2.u.mul(0.12).mul(above)));
      }

      // ── the gate: a colossal ghost ring whose layers turn like an orrery ─────
      // Each band rotates by adding uTime to the angular phase; different rates and
      // signs read as concentric rings counter-spinning. All slow — a full turn
      // takes ~90–200s, so it's alive but never busy.
      {
        const c = vec2(0.34, 0.15);
        const r = length(sv.sub(c));
        const ang = atan(sv.y.sub(c.y), sv.x.sub(c.x));
        const sky = smoothstep(HY - 0.02, HY + 0.06, sv.y);
        const ring2 = crisp(abs(r.sub(0.415)), float(0.0011), px);                                     // solid, still
        const dash = step(0.35, fract(ang.mul(3.8197).add(uTime.mul(0.17))));                          // 24 segments, CW
        const ring1 = crisp(abs(r.sub(0.44)), float(0.0020), px).mul(dash);
        const ticks = crisp(abs(r.sub(0.47)), float(0.007), px).mul(step(0.82, fract(ang.mul(11.459).sub(uTime.mul(0.55))))); // CCW
        const arc = crisp(abs(r.sub(0.62)), float(0.005), px).mul(step(0.80, fract(ang.mul(7.639).add(uTime.mul(0.26)))));    // outer, CW
        const ticks2 = crisp(abs(r.sub(0.393)), float(0.005), px).mul(step(0.86, fract(ang.mul(15.0).add(uTime.mul(0.60))))); // inner, CW
        const innerR = crisp(abs(r.sub(0.30)), float(0.0013), px).mul(step(0.5, fract(ang.mul(2.546).sub(uTime.mul(0.10))))); // small inner ring
        const aura = exp(abs(r.sub(0.42)).mul(-20.0)).mul(0.05);                                       // soft halo around the ring
        col.addAssign(glowC.mul(
          ring1.mul(0.30).add(ring2.mul(0.18)).add(ticks.mul(0.22)).add(arc.mul(0.13))
            .add(ticks2.mul(0.14)).add(innerR.mul(0.16)).add(aura),
        ).mul(sky));
      }

      // sky furniture: falling shards, dust, glitch ticks hugging the horizon
      const ar = screenSize.x.div(screenSize.y);
      const suv = vec2(uv.x.mul(ar), uv.y);
      // scattered triangle shards drifting through the sky, slowly turning
      const shards = (n: number, speed: number, seed: number): N => {
        const p = suv.mul(n).add(vec2(uTime.mul(speed * 0.15), uTime.mul(speed))).add(seed);
        const id = floor(p);
        const h = hash21(id.add(seed));
        const pxc = px.mul(n);
        // (fract(p)-0.5) * rot2(a) — a ROW-vector rotate: (v.x·c − v.y·s, v.x·s + v.y·c)
        const a = h.mul(6.283).add(uTime.mul(0.1).mul(h.sub(0.5)));
        const ca = cos(a), sa = sin(a);
        const f0 = fract(p).sub(0.5);
        const f = vec2(f0.x.mul(ca).sub(f0.y.mul(sa)), f0.x.mul(sa).add(f0.y.mul(ca)));
        const sd = sdTri(f, fract(h.mul(13.7)).mul(0.17).add(0.05));
        const outline = crisp(abs(sd), pxc.mul(0.9), pxc);
        const fill = smoothstep(pxc.negate(), pxc, sd).oneMinus().mul(0.5);
        const shape = mix(outline, max(fill, outline), step(0.72, fract(h.mul(5.19))));
        const tw = sin(uTime.mul(h.add(0.4)).add(h.mul(40.0))).mul(0.35).add(0.65);
        return shape.mul(step(0.905, h)).mul(tw);
      };
      const shardTint = mix(vec3(0.32, 0.58, 1.0), glowC, exp(pow(sv.x.sub(VX).mul(1.2), 2.0).negate()).mul(0.4));
      col.addAssign(shardTint.mul(shards(3.0, 0.012, 0.0).mul(0.18).mul(below.oneMinus())));
      col.addAssign(shardTint.mul(shards(7.0, 0.024, 11.3).mul(0.11).mul(below.oneMinus())));
      // sparse dust motes drifting upward, twinkling
      const dust = (duv: N, n: number, speed: number): N => {
        const p = duv.mul(n).add(vec2(uTime.mul(speed * 0.25), uTime.mul(-speed)));
        const id = floor(p);
        const h = hash21(id);
        const dp = vec2(hash21(id.add(1.7)), hash21(id.add(4.2))).mul(0.8).add(0.1);
        const d = length(fract(p).sub(dp));
        const tw = sin(uTime.mul(h.mul(2.0).add(1.0)).add(h.mul(40.0))).mul(0.45).add(0.55);
        return smoothstep(0.055, 0.0, d).mul(step(0.78, h)).mul(tw);
      };
      const dmote = dust(suv, 22.0, 0.014).mul(0.30).add(dust(suv.add(3.1), 42.0, 0.024).mul(0.16));
      col.addAssign(vec3(0.42, 0.60, 1.0).mul(dmote.mul(0.75)));
      // digital glitch ticks: short bright dashes flickering on hashed rows
      {
        const q = vec2(sv.x, sv.y.mul(2.0));
        const row = floor(q.y.mul(80.0));
        const gt = floor(uTime.mul(5.0));
        const h = hash21(vec2(row, gt));
        const cx = hash21(vec2(row, gt.add(0.5))).mul(1.6).sub(0.8);
        const inRow = step(abs(fract(q.y.mul(80.0)).sub(0.5)), 0.16);
        const glitch = step(0.982, h).mul(step(abs(q.x.sub(cx)), h.mul(0.08).add(0.04))).mul(inRow);
        col.addAssign(glowC.mul(glitch.mul(exp(abs(sv.y.sub(HY)).mul(-8.0))).mul(0.25)));
      }

      // A field of shooting stars: THIN, sharp streaks — a small bright point head
      // with a clean tapering hairline tail — scattered across the sky, each on its
      // own clock so several drift past at once at different depths. Distant and
      // elegant, never a fat falling glow. (9 stars, unrolled at build time.)
      {
        const dl = Math.hypot(1.0, 0.42);
        const dirX = -1.0 / dl, dirY = -0.42 / dl;   // fall down-left; tails trail up-right
        const nrmX = -dirY, nrmY = dirX;
        let met: N | null = null;
        for (let i = 0; i < 9; i++) {
          const fi = i;
          const period = hash21(vec2(fi, 1.7)).mul(7.0).add(6.0); // slow, distant cadences
          const t = uTime.add(fi * 5.31).div(period);
          const slot = floor(t);
          const k = fract(t);                                     // 0..1 flight progress
          const h = hash21(vec2(slot.mul(1.7).add(3.0), fi));
          const fire = step(0.34, h);                             // ~2/3 fire → several always aloft
          // spawn spread across the upper sky; depth (near/far) scales size + brightness
          const far = hash21(vec2(slot.add(9.0), fi + 2.0)).mul(0.6).add(0.4);
          const start = vec2(hash21(vec2(slot, fi + 4.0)).mul(3.6).sub(1.6),
            hash21(vec2(slot.add(5.0), fi)).mul(0.55).add(0.14));
          const head = start.add(vec2(dirX, dirY).mul(k).mul(far.mul(0.7).add(1.5)));
          const rel = sv.sub(head);
          const along = rel.x.mul(dirX).add(rel.y.mul(dirY));     // >0 ahead of head; <0 is the tail
          const perp = rel.x.mul(nrmX).add(rel.y.mul(nrmY));
          const w = px.mul(far.add(1.7));                         // hairline half-width in sv units
          const line = exp(perp.mul(perp).div(w.mul(w)).negate()); // thin sharp streak
          const taillen = far.mul(0.10).add(0.14);
          const tail = smoothstep(taillen.negate(), 0.0, along).mul(step(along, 0.0)); // fades toward tail end
          // small ROUND head: 2D gaussian in both axes
          const headpt = exp(along.mul(along).add(perp.mul(perp)).div(w.mul(w).mul(5.0)).negate());
          const life = smoothstep(0.0, 0.05, k).mul(smoothstep(1.0, 0.74, k));
          const term = line.mul(tail).add(headpt.mul(1.3)).mul(life).mul(fire).mul(far.mul(0.45).add(0.55));
          met = met ? met.add(term) : term;
        }
        col.addAssign(vec3(0.55, 0.74, 1.0).mul(met!.mul(smoothstep(HY, 0.10, sv.y)).mul(1.15)));
      }

      // fine scanlines + grain; a deeper cinematic vignette frames the navy field
      col.mulAssign(sin(frag.y.mul(1.35)).mul(0.030).add(1.0));
      col.addAssign(hash21(frag.add(fract(uTime).mul(913.0))).sub(0.5).mul(0.012));
      col.mulAssign(smoothstep(1.55, 0.28, length(sv)).mul(0.28).add(0.72));
      // lift the deepest shadows a hair toward navy so nothing crushes to flat black
      col.assign(max(col, navyC.mul(0.10)));

      return vec4(max(col, 0.0), 1.0);
    })();
  }

  resize(): void {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.renderer.setSize(w, h, false); // resolution reaches the shader via the screenSize node
    if (this.reduced || this.raf === 0) this.renderOnce();
  }

  private renderOnce(): void {
    if (!this.ready) return; // WebGPU device still initialising (or failed) — nothing to draw with yet
    this.renderer.render(this.scene, this.camera);
  }

  start(): void {
    if (this.disposed) return;
    this.renderOnce();
    if (!this.reduced) this.startLoop();
  }

  /** Re-evaluate whether the ambient loop should run after a live quality / reduced-motion change.
   *  Reduced (Low tier, reduce-effects, or OS reduced-motion) → a single static frame, no ongoing GPU;
   *  otherwise → the ~30fps drift. Called reactively from CyberBackground so the change is instant. */
  refreshMotion(): void {
    if (this.disposed) return;
    if (this.reduced) this.renderOnce();      // the running tick self-stops on this.reduced (below)
    else if (this.raf === 0) this.startLoop(); // was static, now animated → spin it back up
  }

  private startLoop(): void {
    // ~30fps is plenty for a slow ambient drift, and halves the backdrop's GPU time — during a 3D
    // duel this scene shares the GPU with the arena, so its frames are the cheapest ones to give up.
    // Self-terminates if the tier drops to Low / reduced-motion turns on mid-session.
    let last = 0;
    const tick = (now: number): void => {
      if (this.disposed || this.reduced) { this.raf = 0; return; }
      this.raf = requestAnimationFrame(tick);
      if (now - last < 31) return;
      last = now;
      this.uTime.value = (now - this.t0) / 1000;
      this.renderOnce();
    };
    this.raf = requestAnimationFrame(tick);
  }

  dispose(): void {
    this.disposed = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.geometry.dispose();
    this.material.dispose();
    this.renderer.dispose();
  }
}
